import React, { useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Button,
  Badge,
  Icon,
  Divider,
  useToast,
  Tooltip,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Circle,
  IconButton,
  Input,
  useColorMode,
  Switch,
  FormControl,
  FormLabel,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Code,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Progress,
  Alert,
  AlertIcon,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import {
  FaCopy,
  FaCheck,
  FaSun,
  FaCoins,
  FaHome,
  FaArrowRight,
  FaBook,
  FaPalette,
  FaFont,
  FaSquare,
  FaCircle,
  FaGraduationCap,
  FaHeart,
  FaLightbulb,
  FaEye,
  FaMoon,
} from "react-icons/fa";
import { brandConfig, getColor, getComponent } from "../../brandConfig";
import { usePageTitle } from "../../hooks/useDocumentTitle";

// Color swatch component with copy functionality
const ColorSwatch: React.FC<{
  color: string;
  name: string;
  description?: string;
  size?: "sm" | "md" | "lg";
}> = ({ color, name, description, size = "md" }) => {
  const [copied, setCopied] = useState(false);
  const toast = useToast();

  const sizes = {
    sm: { box: "60px", text: "xs" },
    md: { box: "80px", text: "sm" },
    lg: { box: "100px", text: "md" },
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(color);
    setCopied(true);
    toast({
      title: "Copied!",
      description: `${color} copied to clipboard`,
      status: "success",
      duration: 2000,
      isClosable: true,
    });
    setTimeout(() => setCopied(false), 2000);
  };

  // Determine if text should be light or dark based on background
  const isLight = (hex: string) => {
    const c = hex.substring(1);
    const rgb = parseInt(c, 16);
    const r = (rgb >> 16) & 0xff;
    const g = (rgb >> 8) & 0xff;
    const b = (rgb >> 0) & 0xff;
    const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luma > 128;
  };

  const textColor = color.startsWith("#") && isLight(color) ? "gray.800" : "white";

  return (
    <Tooltip label="Click to copy" placement="top">
      <VStack
        spacing={2}
        cursor="pointer"
        onClick={handleCopy}
        transition="all 0.2s"
        _hover={{ transform: "scale(1.05)" }}
      >
        <Box
          w={sizes[size].box}
          h={sizes[size].box}
          bg={color}
          borderRadius="xl"
          display="flex"
          alignItems="center"
          justifyContent="center"
          boxShadow="lg"
          border="2px solid"
          borderColor="whiteAlpha.300"
          position="relative"
          overflow="hidden"
          _before={{
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%)",
          }}
        >
          <Icon
            as={copied ? FaCheck : FaCopy}
            color={textColor}
            boxSize={4}
            opacity={0.8}
          />
        </Box>
        <VStack spacing={0}>
          <Text fontSize={sizes[size].text} fontWeight="600" color="white">
            {name}
          </Text>
          <Code fontSize="xs" bg="whiteAlpha.200" color="gray.300" px={2} borderRadius="md">
            {color}
          </Code>
          {description && (
            <Text fontSize="xs" color="gray.400" textAlign="center" maxW="100px">
              {description}
            </Text>
          )}
        </VStack>
      </VStack>
    </Tooltip>
  );
};

// Section header component
const SectionHeader: React.FC<{
  icon: React.ElementType;
  title: string;
  subtitle?: string;
  color?: string;
}> = ({ icon, title, subtitle, color = "#E8B923" }) => (
  <HStack spacing={4} mb={8}>
    <Circle size="50px" bg={color} boxShadow="0 4px 20px rgba(232, 185, 35, 0.4)">
      <Icon as={icon} boxSize={6} color="white" />
    </Circle>
    <VStack align="start" spacing={0}>
      <Heading size="lg" color="white">
        {title}
      </Heading>
      {subtitle && (
        <Text color="gray.400" fontSize="sm">
          {subtitle}
        </Text>
      )}
    </VStack>
  </HStack>
);

// Glass card component
const GlassCard: React.FC<{ children: React.ReactNode; p?: number }> = ({
  children,
  p = 8,
}) => (
  <Box
    bg="linear-gradient(135deg, rgba(0, 31, 63, 0.95) 0%, rgba(0, 21, 42, 0.9) 100%)"
    backdropFilter="blur(20px)"
    borderRadius="2xl"
    border="1px solid"
    borderColor="whiteAlpha.100"
    p={p}
    boxShadow="0 8px 32px rgba(0, 0, 0, 0.4)"
  >
    {children}
  </Box>
);

const BrandStyleGuide: React.FC = () => {
  usePageTitle("Brand Style Guide");
  const { colorMode, toggleColorMode } = useColorMode();
  const toast = useToast();

  // Primary brand colors
  const primaryColors = [
    { color: "#001f3f", name: "Deep Navy", description: "Hero backgrounds" },
    { color: "#101c47", name: "Navy", description: "Primary brand" },
    { color: "#FFFFFF", name: "White", description: "Text & cards" },
  ];

  // Highlight colors (new recommendations)
  const highlightColors = [
    { color: "#E8B923", name: "Illuminated Gold", description: "Primary CTA" },
    { color: "#D4A843", name: "Gold Hover", description: "Hover state" },
    { color: "#14B8A6", name: "Clarity Teal", description: "Secondary CTA" },
    { color: "#0D9488", name: "Teal Hover", description: "Hover state" },
    { color: "#F97316", name: "Warm Ember", description: "Urgency accent" },
  ];

  // Section-specific colors
  const sectionColors = [
    { color: "#E8B923", name: "Solar Gold", description: "Solar division" },
    { color: "#E8B923", name: "Finance Gold", description: "Finance division" },
    { color: "#14B8A6", name: "Property Teal", description: "Property division" },
  ];

  // Status colors
  const statusColors = [
    { color: "#30D158", name: "Success", description: "Confirmations" },
    { color: "#FF9F0A", name: "Warning", description: "Caution" },
    { color: "#FF3B30", name: "Error", description: "Errors" },
    { color: "#007AFF", name: "Info", description: "Information" },
  ];

  return (
    <>

      {/* Hero Section */}
      <Box
        position="relative"
        py={20}
        overflow="hidden"
        backgroundImage="url('https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment="fixed"
        _before={{
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bg: "rgba(0, 31, 63, 0.92)",
        }}
        _after={{
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "radial-gradient(circle at 30% 20%, rgba(232, 185, 35, 0.15) 0%, transparent 40%), radial-gradient(circle at 70% 80%, rgba(20, 184, 166, 0.1) 0%, transparent 40%)",
        }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={6} textAlign="center">
            <Badge
              bg="linear-gradient(135deg, #E8B923 0%, #D4A843 100%)"
              color="white"
              px={4}
              py={2}
              borderRadius="full"
              fontSize="sm"
              textTransform="uppercase"
              letterSpacing="wider"
              boxShadow="0 4px 15px rgba(232, 185, 35, 0.4)"
            >
              Brand Guidelines v1.0
            </Badge>
            <Heading
              size="3xl"
              bgGradient="linear(to-r, white, gray.300)"
              bgClip="text"
              fontWeight="black"
            >
              One Group Brand System
            </Heading>
            <Text fontSize="xl" color="gray.400" maxW="2xl">
              A comprehensive visual identity guide built on the{" "}
              <Text as="span" color="#E8B923" fontWeight="600">
                Sage Archetype
              </Text>{" "}
              — wisdom, clarity, and trusted guidance.
            </Text>

            {/* Quick stats */}
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8} pt={8}>
              {[
                { label: "Primary Colors", value: "3" },
                { label: "Highlight Colors", value: "5" },
                { label: "Typography Styles", value: "6" },
                { label: "Components", value: "12+" },
              ].map((stat, idx) => (
                <VStack key={idx}>
                  <Text fontSize="3xl" fontWeight="bold" color="#E8B923">
                    {stat.value}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    {stat.label}
                  </Text>
                </VStack>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      <Container maxW="container.xl" py={12}>
        <Tabs variant="soft-rounded" colorScheme="yellow">
          <TabList
            bg="rgba(0, 31, 63, 0.6)"
            border="1px solid"
            borderColor="whiteAlpha.100"
            p={2}
            borderRadius="2xl"
            flexWrap="wrap"
            justifyContent="center"
          >
            <Tab color="gray.400" _selected={{ color: "white", bg: "#E8B923" }}>
              <Icon as={FaPalette} mr={2} /> Colors
            </Tab>
            <Tab color="gray.400" _selected={{ color: "white", bg: "#E8B923" }}>
              <Icon as={FaFont} mr={2} /> Typography
            </Tab>
            <Tab color="gray.400" _selected={{ color: "white", bg: "#E8B923" }}>
              <Icon as={FaSquare} mr={2} /> Components
            </Tab>
            <Tab color="gray.400" _selected={{ color: "white", bg: "#E8B923" }}>
              <Icon as={FaGraduationCap} mr={2} /> Archetype
            </Tab>
            <Tab color="gray.400" _selected={{ color: "white", bg: "#E8B923" }}>
              <Icon as={FaBook} mr={2} /> Usage
            </Tab>
          </TabList>

          <TabPanels mt={8}>
            {/* Colors Panel */}
            <TabPanel px={0}>
              <VStack spacing={12} align="stretch">
                {/* Primary Colors */}
                <GlassCard>
                  <SectionHeader
                    icon={FaCircle}
                    title="Foundation Colors"
                    subtitle="The core palette that establishes trust and authority"
                  />
                  <SimpleGrid columns={{ base: 3, md: 3 }} spacing={8}>
                    {primaryColors.map((c, idx) => (
                      <ColorSwatch key={idx} {...c} size="lg" />
                    ))}
                  </SimpleGrid>
                </GlassCard>

                {/* Highlight Colors */}
                <GlassCard>
                  <SectionHeader
                    icon={FaLightbulb}
                    title="Highlight Colors"
                    subtitle="Strategic accents for CTAs and visual energy"
                    color="#E8B923"
                  />
                  <SimpleGrid columns={{ base: 3, md: 5 }} spacing={6}>
                    {highlightColors.map((c, idx) => (
                      <ColorSwatch key={idx} {...c} />
                    ))}
                  </SimpleGrid>

                  <Divider my={8} borderColor="whiteAlpha.100" />

                  {/* Color Usage Guide */}
                  <Heading size="md" color="white" mb={4}>
                    CTA Hierarchy
                  </Heading>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <Box p={4} bg="whiteAlpha.50" borderRadius="xl">
                      <Text color="#E8B923" fontWeight="600" mb={2}>
                        Primary CTA
                      </Text>
                      <Button
                        bg="#E8B923"
                        color="white"
                        size="lg"
                        w="full"
                        _hover={{ bg: "#D4A843" }}
                        rightIcon={<FaArrowRight />}
                      >
                        Book Consultation
                      </Button>
                      <Text fontSize="xs" color="gray.500" mt={2}>
                        Use for main conversion actions
                      </Text>
                    </Box>
                    <Box p={4} bg="whiteAlpha.50" borderRadius="xl">
                      <Text color="#14B8A6" fontWeight="600" mb={2}>
                        Secondary CTA
                      </Text>
                      <Button
                        bg="#14B8A6"
                        color="white"
                        size="lg"
                        w="full"
                        _hover={{ bg: "#0D9488" }}
                        rightIcon={<FaArrowRight />}
                      >
                        Learn More
                      </Button>
                      <Text fontSize="xs" color="gray.500" mt={2}>
                        Use for secondary actions
                      </Text>
                    </Box>
                    <Box p={4} bg="whiteAlpha.50" borderRadius="xl">
                      <Text color="white" fontWeight="600" mb={2}>
                        Tertiary CTA
                      </Text>
                      <Button
                        variant="outline"
                        borderColor="white"
                        color="white"
                        size="lg"
                        w="full"
                        _hover={{ bg: "whiteAlpha.100" }}
                      >
                        View Details
                      </Button>
                      <Text fontSize="xs" color="gray.500" mt={2}>
                        Use for lower priority actions
                      </Text>
                    </Box>
                  </SimpleGrid>
                </GlassCard>

                {/* Section Colors */}
                <GlassCard>
                  <SectionHeader
                    icon={FaHome}
                    title="Division Colors"
                    subtitle="Consistent identification across service verticals"
                    color="#14B8A6"
                  />
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                    {/* Solar */}
                    <Box
                      p={6}
                      borderRadius="xl"
                      border="3px solid #E8B923"
                      bg="linear-gradient(135deg, rgba(232, 185, 35, 0.1) 0%, transparent 100%)"
                    >
                      <HStack mb={4}>
                        <Icon as={FaSun} boxSize={8} color="#E8B923" />
                        <VStack align="start" spacing={0}>
                          <Text color="white" fontWeight="bold">
                            Solar Division
                          </Text>
                          <Code fontSize="xs" bg="transparent" color="#E8B923">
                            #E8B923
                          </Code>
                        </VStack>
                      </HStack>
                      <Text fontSize="sm" color="gray.400">
                        Energy, sunlight, value creation. Gold represents the power and
                        premium nature of solar investment.
                      </Text>
                    </Box>

                    {/* Finance */}
                    <Box
                      p={6}
                      borderRadius="xl"
                      border="3px solid #E8B923"
                      bg="linear-gradient(135deg, rgba(232, 185, 35, 0.1) 0%, transparent 100%)"
                    >
                      <HStack mb={4}>
                        <Icon as={FaCoins} boxSize={8} color="#E8B923" />
                        <VStack align="start" spacing={0}>
                          <Text color="white" fontWeight="bold">
                            Finance Division
                          </Text>
                          <Code fontSize="xs" bg="transparent" color="#E8B923">
                            #E8B923
                          </Code>
                        </VStack>
                      </HStack>
                      <Text fontSize="sm" color="gray.400">
                        Wealth, achievement, financial success. Gold reinforces the
                        premium positioning and prosperity.
                      </Text>
                    </Box>

                    {/* Property */}
                    <Box
                      p={6}
                      borderRadius="xl"
                      border="3px solid #14B8A6"
                      bg="linear-gradient(135deg, rgba(20, 184, 166, 0.1) 0%, transparent 100%)"
                    >
                      <HStack mb={4}>
                        <Icon as={FaHome} boxSize={8} color="#14B8A6" />
                        <VStack align="start" spacing={0}>
                          <Text color="white" fontWeight="bold">
                            Property Division
                          </Text>
                          <Code fontSize="xs" bg="transparent" color="#14B8A6">
                            #14B8A6
                          </Code>
                        </VStack>
                      </HStack>
                      <Text fontSize="sm" color="gray.400">
                        Growth, building, long-term investment. Teal differentiates
                        property while conveying stability and progress.
                      </Text>
                    </Box>
                  </SimpleGrid>
                </GlassCard>

                {/* Background System */}
                <GlassCard>
                  <SectionHeader
                    icon={FaMoon}
                    title="Background System"
                    subtitle="Navy-first approach for trust and authority"
                    color="#001f3f"
                  />

                  <Alert status="info" mb={6} borderRadius="xl" bg="rgba(232, 185, 35, 0.1)" border="1px solid rgba(232, 185, 35, 0.3)">
                    <AlertIcon color="#E8B923" />
                    <Text color="white" fontSize="sm">
                      <Text as="span" fontWeight="bold">Why Navy?</Text> Research shows navy is the #1 color associated with trustworthiness in financial services.
                      It conveys established credibility, depth, and wisdom — perfect for the Sage archetype.
                    </Text>
                  </Alert>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={8}>
                    {/* Hero with Image Overlay */}
                    <Box>
                      <Text color="gray.400" fontSize="sm" mb={3}>
                        HERO WITH IMAGE OVERLAY
                      </Text>
                      <Box
                        h="200px"
                        borderRadius="xl"
                        position="relative"
                        overflow="hidden"
                        backgroundImage="url('https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80')"
                        backgroundSize="cover"
                        backgroundPosition="center"
                        _before={{
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          bg: "rgba(0, 31, 63, 0.85)",
                        }}
                      >
                        <VStack
                          position="relative"
                          zIndex={1}
                          h="full"
                          justify="center"
                          spacing={2}
                        >
                          <Text color="white" fontWeight="bold" fontSize="xl">
                            Hero Headline
                          </Text>
                          <Text color="gray.300" fontSize="sm">
                            Subheadline text here
                          </Text>
                          <Button bg="#E8B923" color="white" size="sm" _hover={{ bg: "#D4A843" }}>
                            Primary CTA
                          </Button>
                        </VStack>
                      </Box>
                      <Code mt={2} fontSize="xs" bg="whiteAlpha.100" color="gray.300" p={2} borderRadius="md" display="block">
                        overlay: rgba(0, 31, 63, 0.85)
                      </Code>
                    </Box>

                    {/* Solid Navy Section */}
                    <Box>
                      <Text color="gray.400" fontSize="sm" mb={3}>
                        SOLID NAVY SECTION
                      </Text>
                      <Box
                        h="200px"
                        borderRadius="xl"
                        bg="#001f3f"
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        p={6}
                      >
                        <Text fontSize="4xl" fontWeight="bold" color="#E8B923">
                          $2B+
                        </Text>
                        <Text color="white" fontWeight="600">
                          Loans Settled
                        </Text>
                        <Text color="gray.400" fontSize="sm">
                          Perfect for stats & feature sections
                        </Text>
                      </Box>
                      <Code mt={2} fontSize="xs" bg="whiteAlpha.100" color="gray.300" p={2} borderRadius="md" display="block">
                        solid: #001f3f
                      </Code>
                    </Box>
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                    {/* Navy Gradient */}
                    <Box>
                      <Text color="gray.400" fontSize="sm" mb={3}>
                        NAVY GRADIENT
                      </Text>
                      <Box
                        h="120px"
                        borderRadius="xl"
                        bg="linear-gradient(135deg, #001f3f 0%, #003366 50%, #001f3f 100%)"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text color="white" fontWeight="600">Depth & Movement</Text>
                      </Box>
                    </Box>

                    {/* White Cards on Navy */}
                    <Box>
                      <Text color="gray.400" fontSize="sm" mb={3}>
                        WHITE CARDS ON NAVY
                      </Text>
                      <Box
                        h="120px"
                        borderRadius="xl"
                        bg="#001f3f"
                        p={4}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Box bg="white" p={4} borderRadius="lg" boxShadow="lg">
                          <Text color="gray.800" fontWeight="600" fontSize="sm">Card Content</Text>
                        </Box>
                      </Box>
                    </Box>

                    {/* Glass on Navy */}
                    <Box>
                      <Text color="gray.400" fontSize="sm" mb={3}>
                        GLASS EFFECT ON NAVY
                      </Text>
                      <Box
                        h="120px"
                        borderRadius="xl"
                        bg="#001f3f"
                        p={4}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Box
                          bg="rgba(255, 255, 255, 0.1)"
                          backdropFilter="blur(10px)"
                          border="1px solid rgba(255, 255, 255, 0.2)"
                          p={4}
                          borderRadius="lg"
                        >
                          <Text color="white" fontWeight="600" fontSize="sm">Glass Card</Text>
                        </Box>
                      </Box>
                    </Box>
                  </SimpleGrid>
                </GlassCard>

                {/* Status Colors */}
                <GlassCard>
                  <SectionHeader
                    icon={FaEye}
                    title="Status Colors"
                    subtitle="System feedback and state indicators"
                    color="#30D158"
                  />
                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6}>
                    {statusColors.map((c, idx) => (
                      <ColorSwatch key={idx} {...c} />
                    ))}
                  </SimpleGrid>
                </GlassCard>
              </VStack>
            </TabPanel>

            {/* Typography Panel */}
            <TabPanel px={0}>
              <VStack spacing={12} align="stretch">
                <GlassCard>
                  <SectionHeader
                    icon={FaFont}
                    title="Typography System"
                    subtitle="Font families and text hierarchy"
                  />

                  {/* Font Family */}
                  <Box mb={8}>
                    <Text color="gray.400" fontSize="sm" mb={4}>
                      PRIMARY FONT FAMILY
                    </Text>
                    <HStack
                      p={6}
                      bg="whiteAlpha.50"
                      borderRadius="xl"
                      justify="space-between"
                      flexWrap="wrap"
                    >
                      <VStack align="start">
                        <Text
                          fontSize="4xl"
                          fontWeight="bold"
                          color="white"
                          fontFamily={brandConfig.fonts.heading}
                        >
                          Geist
                        </Text>
                        <Text color="gray.400" fontSize="sm">
                          Used for headings and body text
                        </Text>
                      </VStack>
                      <Code
                        p={3}
                        borderRadius="md"
                        bg="whiteAlpha.100"
                        color="gray.300"
                        fontSize="xs"
                      >
                        {brandConfig.fonts.heading}
                      </Code>
                    </HStack>
                  </Box>

                  {/* Type Scale */}
                  <Text color="gray.400" fontSize="sm" mb={4}>
                    TYPE SCALE
                  </Text>
                  <VStack spacing={4} align="stretch">
                    {[
                      { size: "4xl", weight: "bold", label: "Display", sample: "Build Your Future" },
                      { size: "3xl", weight: "bold", label: "H1", sample: "One Group Australasia" },
                      { size: "2xl", weight: "bold", label: "H2", sample: "Solar, Finance & Property" },
                      { size: "xl", weight: "semibold", label: "H3", sample: "Our Three Core Services" },
                      { size: "lg", weight: "medium", label: "H4", sample: "Data-Driven Investment" },
                      { size: "md", weight: "normal", label: "Body", sample: "We help high-income earners build retirement wealth beyond superannuation through integrated solar, finance, and property solutions." },
                      { size: "sm", weight: "normal", label: "Small", sample: "Terms and conditions apply. Australian Credit License #123456." },
                    ].map((item, idx) => (
                      <HStack
                        key={idx}
                        p={4}
                        bg="whiteAlpha.50"
                        borderRadius="xl"
                        justify="space-between"
                        align="center"
                      >
                        <HStack spacing={4}>
                          <Badge
                            bg="#E8B923"
                            color="white"
                            px={3}
                            py={1}
                            borderRadius="md"
                            minW="60px"
                            textAlign="center"
                          >
                            {item.label}
                          </Badge>
                          <Text
                            fontSize={item.size}
                            fontWeight={item.weight}
                            color="white"
                            noOfLines={1}
                            maxW="500px"
                          >
                            {item.sample}
                          </Text>
                        </HStack>
                        <Code fontSize="xs" bg="whiteAlpha.100" color="gray.400" px={2}>
                          {item.size} / {item.weight}
                        </Code>
                      </HStack>
                    ))}
                  </VStack>
                </GlassCard>

                {/* Text Colors */}
                <GlassCard>
                  <SectionHeader
                    icon={FaPalette}
                    title="Text Colors"
                    subtitle="Hierarchy and contrast guidelines"
                    color="#14B8A6"
                  />
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                    {/* Navy backgrounds */}
                    <Box>
                      <Text color="gray.400" fontSize="sm" mb={4}>
                        ON NAVY BACKGROUNDS
                      </Text>
                      <VStack
                        spacing={3}
                        p={6}
                        bg="#001f3f"
                        borderRadius="xl"
                        align="start"
                      >
                        <Text color="white" fontWeight="bold">
                          Primary Text — White (#FFFFFF)
                        </Text>
                        <Text color="rgba(255, 255, 255, 0.7)">
                          Secondary Text — 70% White
                        </Text>
                        <Text color="rgba(255, 255, 255, 0.5)">
                          Muted Text — 50% White
                        </Text>
                        <Text color="#E8B923" fontWeight="600">
                          Accent Text — Gold (#E8B923)
                        </Text>
                      </VStack>
                    </Box>

                    {/* Light backgrounds */}
                    <Box>
                      <Text color="gray.400" fontSize="sm" mb={4}>
                        ON LIGHT BACKGROUNDS
                      </Text>
                      <VStack
                        spacing={3}
                        p={6}
                        bg="white"
                        borderRadius="xl"
                        align="start"
                      >
                        <Text color="#1D1D1F" fontWeight="bold">
                          Primary Text — Near Black (#1D1D1F)
                        </Text>
                        <Text color="#6E6E73">Secondary Text — Gray (#6E6E73)</Text>
                        <Text color="#8E8E93">Muted Text — Light Gray (#8E8E93)</Text>
                        <Text color="#101c47" fontWeight="600">
                          Accent Text — Navy (#101c47)
                        </Text>
                      </VStack>
                    </Box>
                  </SimpleGrid>
                </GlassCard>
              </VStack>
            </TabPanel>

            {/* Components Panel */}
            <TabPanel px={0}>
              <VStack spacing={12} align="stretch">
                {/* Buttons */}
                <GlassCard>
                  <SectionHeader
                    icon={FaSquare}
                    title="Buttons"
                    subtitle="Interactive elements and states"
                  />
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                    {/* Primary Buttons */}
                    <Box>
                      <Text color="gray.400" fontSize="sm" mb={4}>
                        PRIMARY BUTTONS
                      </Text>
                      <VStack spacing={4} align="stretch">
                        <Button
                          bg="#E8B923"
                          color="white"
                          size="lg"
                          _hover={{ bg: "#D4A843" }}
                          rightIcon={<FaArrowRight />}
                        >
                          Get Started Today
                        </Button>
                        <Button
                          bg="#14B8A6"
                          color="white"
                          size="lg"
                          _hover={{ bg: "#0D9488" }}
                          rightIcon={<FaArrowRight />}
                        >
                          Learn More
                        </Button>
                        <Button
                          bg="#101c47"
                          color="white"
                          size="lg"
                          _hover={{ bg: "#0d1636" }}
                          rightIcon={<FaArrowRight />}
                        >
                          Contact Us
                        </Button>
                      </VStack>
                    </Box>

                    {/* Secondary Buttons */}
                    <Box>
                      <Text color="gray.400" fontSize="sm" mb={4}>
                        SECONDARY & OUTLINE
                      </Text>
                      <VStack spacing={4} align="stretch">
                        <Button
                          variant="outline"
                          borderColor="#E8B923"
                          color="#E8B923"
                          size="lg"
                          _hover={{ bg: "rgba(232, 185, 35, 0.1)" }}
                        >
                          View Services
                        </Button>
                        <Button
                          variant="outline"
                          borderColor="white"
                          color="white"
                          size="lg"
                          _hover={{ bg: "whiteAlpha.100" }}
                        >
                          Download Guide
                        </Button>
                        <Button
                          variant="ghost"
                          color="#E8B923"
                          size="lg"
                          rightIcon={<FaArrowRight />}
                          _hover={{ bg: "rgba(232, 185, 35, 0.1)" }}
                        >
                          Read Case Study
                        </Button>
                      </VStack>
                    </Box>
                  </SimpleGrid>

                  <Divider my={8} borderColor="whiteAlpha.100" />

                  {/* Button Sizes */}
                  <Text color="gray.400" fontSize="sm" mb={4}>
                    BUTTON SIZES
                  </Text>
                  <HStack spacing={4} flexWrap="wrap">
                    <Button bg="#E8B923" color="white" size="xs">
                      Extra Small
                    </Button>
                    <Button bg="#E8B923" color="white" size="sm">
                      Small
                    </Button>
                    <Button bg="#E8B923" color="white" size="md">
                      Medium
                    </Button>
                    <Button bg="#E8B923" color="white" size="lg">
                      Large
                    </Button>
                  </HStack>
                </GlassCard>

                {/* Badges */}
                <GlassCard>
                  <SectionHeader
                    icon={FaCircle}
                    title="Badges & Tags"
                    subtitle="Labels and status indicators"
                    color="#14B8A6"
                  />
                  <Wrap spacing={4}>
                    <WrapItem>
                      <Badge
                        bg="#E8B923"
                        color="white"
                        px={3}
                        py={1}
                        borderRadius="full"
                      >
                        Featured
                      </Badge>
                    </WrapItem>
                    <WrapItem>
                      <Badge
                        bg="#14B8A6"
                        color="white"
                        px={3}
                        py={1}
                        borderRadius="full"
                      >
                        New
                      </Badge>
                    </WrapItem>
                    <WrapItem>
                      <Badge
                        bg="#30D158"
                        color="white"
                        px={3}
                        py={1}
                        borderRadius="full"
                      >
                        Active
                      </Badge>
                    </WrapItem>
                    <WrapItem>
                      <Badge
                        bg="#FF9F0A"
                        color="white"
                        px={3}
                        py={1}
                        borderRadius="full"
                      >
                        Pending
                      </Badge>
                    </WrapItem>
                    <WrapItem>
                      <Badge
                        bg="#FF3B30"
                        color="white"
                        px={3}
                        py={1}
                        borderRadius="full"
                      >
                        Urgent
                      </Badge>
                    </WrapItem>
                    <WrapItem>
                      <Badge
                        variant="outline"
                        borderColor="#E8B923"
                        color="#E8B923"
                        px={3}
                        py={1}
                        borderRadius="full"
                      >
                        Solar
                      </Badge>
                    </WrapItem>
                    <WrapItem>
                      <Badge
                        variant="outline"
                        borderColor="#14B8A6"
                        color="#14B8A6"
                        px={3}
                        py={1}
                        borderRadius="full"
                      >
                        Property
                      </Badge>
                    </WrapItem>
                  </Wrap>
                </GlassCard>

                {/* Cards */}
                <GlassCard>
                  <SectionHeader
                    icon={FaSquare}
                    title="Cards"
                    subtitle="Content containers and layouts"
                    color="#E8B923"
                  />
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                    {/* Service Card */}
                    <Box
                      p={6}
                      bg="white"
                      borderRadius="xl"
                      border="3px solid #E8B923"
                      _hover={{ transform: "translateY(-4px)", shadow: "xl" }}
                      transition="all 0.3s"
                    >
                      <Icon as={FaSun} boxSize={10} color="#E8B923" mb={4} />
                      <Heading size="md" color="gray.800" mb={2}>
                        Solar Energy
                      </Heading>
                      <Text color="gray.600" fontSize="sm" mb={4}>
                        Residential & commercial solar solutions
                      </Text>
                      <Button
                        bg="#E8B923"
                        color="white"
                        size="sm"
                        _hover={{ bg: "#D4A843" }}
                      >
                        Explore →
                      </Button>
                    </Box>

                    {/* Glass Card on Navy */}
                    <Box
                      p={6}
                      bg="rgba(255, 255, 255, 0.08)"
                      borderRadius="xl"
                      border="1px solid"
                      borderColor="whiteAlpha.200"
                      backdropFilter="blur(10px)"
                    >
                      <Icon as={FaCoins} boxSize={10} color="#E8B923" mb={4} />
                      <Heading size="md" color="white" mb={2}>
                        Finance
                      </Heading>
                      <Text color="gray.300" fontSize="sm" mb={4}>
                        Home loans & wealth management
                      </Text>
                      <Button
                        variant="outline"
                        borderColor="#E8B923"
                        color="#E8B923"
                        size="sm"
                      >
                        Explore →
                      </Button>
                    </Box>

                    {/* Stat Card on Deep Navy */}
                    <Box
                      p={6}
                      bg="#001f3f"
                      borderRadius="xl"
                      textAlign="center"
                    >
                      <Text fontSize="4xl" fontWeight="bold" color="#E8B923">
                        $2B+
                      </Text>
                      <Text color="white" fontWeight="600">
                        Loans Settled
                      </Text>
                      <Text color="gray.300" fontSize="sm">
                        Since 2008
                      </Text>
                    </Box>
                  </SimpleGrid>
                </GlassCard>

                {/* Form Elements */}
                <GlassCard>
                  <SectionHeader
                    icon={FaSquare}
                    title="Form Elements"
                    subtitle="Inputs and interactive controls"
                    color="#14B8A6"
                  />
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                    <VStack spacing={4} align="stretch">
                      <FormControl>
                        <FormLabel color="white">Email Address</FormLabel>
                        <Input
                          placeholder="you@example.com"
                          bg="whiteAlpha.100"
                          border="1px solid"
                          borderColor="whiteAlpha.200"
                          color="white"
                          _placeholder={{ color: "gray.500" }}
                          _focus={{
                            borderColor: "#E8B923",
                            boxShadow: "0 0 0 1px #E8B923",
                          }}
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel color="white">Investment Amount</FormLabel>
                        <Input
                          placeholder="$500,000"
                          bg="whiteAlpha.100"
                          border="1px solid"
                          borderColor="whiteAlpha.200"
                          color="white"
                          _focus={{
                            borderColor: "#14B8A6",
                            boxShadow: "0 0 0 1px #14B8A6",
                          }}
                        />
                      </FormControl>
                    </VStack>
                    <VStack spacing={4} align="stretch">
                      <FormControl display="flex" alignItems="center">
                        <FormLabel color="white" mb={0}>
                          Receive updates
                        </FormLabel>
                        <Switch colorScheme="yellow" />
                      </FormControl>
                      <FormControl display="flex" alignItems="center">
                        <FormLabel color="white" mb={0}>
                          Newsletter
                        </FormLabel>
                        <Switch colorScheme="teal" defaultChecked />
                      </FormControl>
                      <Progress
                        value={65}
                        size="lg"
                        borderRadius="full"
                        bg="whiteAlpha.200"
                        sx={{
                          "& > div": {
                            background: "linear-gradient(90deg, #E8B923 0%, #14B8A6 100%)",
                          },
                        }}
                      />
                      <Text color="gray.400" fontSize="sm">
                        Portfolio Progress: 65%
                      </Text>
                    </VStack>
                  </SimpleGrid>
                </GlassCard>

                {/* Alerts */}
                <GlassCard>
                  <SectionHeader
                    icon={FaEye}
                    title="Alerts & Notifications"
                    subtitle="System messages and feedback"
                    color="#30D158"
                  />
                  <VStack spacing={4} align="stretch">
                    <Alert status="success" borderRadius="xl" bg="rgba(48, 209, 88, 0.15)" border="1px solid" borderColor="rgba(48, 209, 88, 0.3)">
                      <AlertIcon color="#30D158" />
                      <Text color="white">Your consultation has been booked successfully!</Text>
                    </Alert>
                    <Alert status="info" borderRadius="xl" bg="rgba(0, 122, 255, 0.15)" border="1px solid" borderColor="rgba(0, 122, 255, 0.3)">
                      <AlertIcon color="#007AFF" />
                      <Text color="white">Pre-approval typically takes 24-48 hours.</Text>
                    </Alert>
                    <Alert status="warning" borderRadius="xl" bg="rgba(255, 159, 10, 0.15)" border="1px solid" borderColor="rgba(255, 159, 10, 0.3)">
                      <AlertIcon color="#FF9F0A" />
                      <Text color="white">Please complete your profile to continue.</Text>
                    </Alert>
                    <Alert status="error" borderRadius="xl" bg="rgba(255, 59, 48, 0.15)" border="1px solid" borderColor="rgba(255, 59, 48, 0.3)">
                      <AlertIcon color="#FF3B30" />
                      <Text color="white">Unable to process your request. Please try again.</Text>
                    </Alert>
                  </VStack>
                </GlassCard>
              </VStack>
            </TabPanel>

            {/* Archetype Panel */}
            <TabPanel px={0}>
              <VStack spacing={12} align="stretch">
                <GlassCard>
                  <SectionHeader
                    icon={FaGraduationCap}
                    title="The Sage Archetype"
                    subtitle="One Group's brand personality foundation"
                  />

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                    <Box>
                      <VStack spacing={6} align="start">
                        <Box>
                          <Text color="#E8B923" fontWeight="600" fontSize="sm" mb={2}>
                            CORE MOTIVATION
                          </Text>
                          <Text color="white" fontSize="lg">
                            To understand the world and share knowledge that helps others make
                            informed decisions.
                          </Text>
                        </Box>
                        <Box>
                          <Text color="#E8B923" fontWeight="600" fontSize="sm" mb={2}>
                            BRAND PROMISE
                          </Text>
                          <Text color="white" fontSize="lg">
                            "We have the expertise to guide you, and we genuinely care about
                            your outcomes."
                          </Text>
                        </Box>
                        <Box>
                          <Text color="#E8B923" fontWeight="600" fontSize="sm" mb={2}>
                            KEY TRAITS
                          </Text>
                          <Wrap spacing={2}>
                            {[
                              "Wisdom",
                              "Expertise",
                              "Clarity",
                              "Trust",
                              "Patience",
                              "Understanding",
                            ].map((trait) => (
                              <WrapItem key={trait}>
                                <Badge
                                  bg="whiteAlpha.100"
                                  color="white"
                                  px={3}
                                  py={1}
                                  borderRadius="full"
                                >
                                  {trait}
                                </Badge>
                              </WrapItem>
                            ))}
                          </Wrap>
                        </Box>
                      </VStack>
                    </Box>

                    <Box
                      p={6}
                      bg="linear-gradient(135deg, rgba(232, 185, 35, 0.1) 0%, rgba(20, 184, 166, 0.1) 100%)"
                      borderRadius="xl"
                      border="1px solid"
                      borderColor="whiteAlpha.200"
                    >
                      <Text color="#E8B923" fontWeight="600" fontSize="sm" mb={4}>
                        SECONDARY: CAREGIVER
                      </Text>
                      <VStack spacing={3} align="start">
                        <HStack>
                          <Icon as={FaHeart} color="#14B8A6" />
                          <Text color="white">"We're with you every step"</Text>
                        </HStack>
                        <HStack>
                          <Icon as={FaHeart} color="#14B8A6" />
                          <Text color="white">Ongoing monitoring & support</Text>
                        </HStack>
                        <HStack>
                          <Icon as={FaHeart} color="#14B8A6" />
                          <Text color="white">95-98% client satisfaction</Text>
                        </HStack>
                        <HStack>
                          <Icon as={FaHeart} color="#14B8A6" />
                          <Text color="white">Partners in wealth building</Text>
                        </HStack>
                      </VStack>
                    </Box>
                  </SimpleGrid>

                  <Divider my={8} borderColor="whiteAlpha.100" />

                  {/* Sage Alignment Table */}
                  <Text color="gray.400" fontSize="sm" mb={4}>
                    ARCHETYPE ALIGNMENT
                  </Text>
                  <Box overflowX="auto">
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th color="gray.400" borderColor="whiteAlpha.100">
                            One Group Characteristic
                          </Th>
                          <Th color="gray.400" borderColor="whiteAlpha.100">
                            Sage Alignment
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {[
                          [
                            "Data-driven analysis & research-backed decisions",
                            "Core Sage trait: wisdom through knowledge",
                          ],
                          [
                            '"Remove the guesswork from investment"',
                            "Sage promise: clarity through understanding",
                          ],
                          [
                            "Integrated ecosystem approach",
                            "Sage vision: seeing the whole picture",
                          ],
                          [
                            "Clear, practical strategies",
                            "Sage values: truth over hype",
                          ],
                          [
                            "Helping clients make informed decisions",
                            "Sage purpose: guide with expertise",
                          ],
                          [
                            "Long-term planning over short-term gains",
                            "Sage patience and perspective",
                          ],
                        ].map(([char, align], idx) => (
                          <Tr key={idx}>
                            <Td color="white" borderColor="whiteAlpha.100">
                              {char}
                            </Td>
                            <Td color="#E8B923" borderColor="whiteAlpha.100">
                              {align}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </GlassCard>

                {/* Voice & Tone */}
                <GlassCard>
                  <SectionHeader
                    icon={FaBook}
                    title="Voice & Tone"
                    subtitle="How One Group communicates"
                    color="#14B8A6"
                  />

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                    {/* Do */}
                    <Box>
                      <HStack mb={4}>
                        <Circle size="30px" bg="#30D158">
                          <Icon as={FaCheck} boxSize={3} color="white" />
                        </Circle>
                        <Text color="white" fontWeight="bold">
                          DO
                        </Text>
                      </HStack>
                      <VStack spacing={3} align="start">
                        {[
                          "Use data and research to support claims",
                          "Speak with measured confidence",
                          "Emphasize long-term outcomes",
                          "Acknowledge complexity, then simplify",
                          "Focus on client education",
                          "Be direct and clear",
                        ].map((item, idx) => (
                          <HStack key={idx}>
                            <Icon as={FaCheck} color="#30D158" boxSize={3} />
                            <Text color="gray.300">{item}</Text>
                          </HStack>
                        ))}
                      </VStack>
                    </Box>

                    {/* Don't */}
                    <Box>
                      <HStack mb={4}>
                        <Circle size="30px" bg="#FF3B30">
                          <Text color="white" fontWeight="bold" fontSize="sm">
                            ✕
                          </Text>
                        </Circle>
                        <Text color="white" fontWeight="bold">
                          DON'T
                        </Text>
                      </HStack>
                      <VStack spacing={3} align="start">
                        {[
                          "Use hype or pressure tactics",
                          '"Get rich quick" language',
                          "Oversimplify important decisions",
                          "Make promises without evidence",
                          "Use aggressive urgency",
                          "Talk down to the audience",
                        ].map((item, idx) => (
                          <HStack key={idx}>
                            <Text color="#FF3B30" fontWeight="bold">
                              ✕
                            </Text>
                            <Text color="gray.300">{item}</Text>
                          </HStack>
                        ))}
                      </VStack>
                    </Box>
                  </SimpleGrid>

                  <Divider my={8} borderColor="whiteAlpha.100" />

                  {/* Example Copy */}
                  <Text color="gray.400" fontSize="sm" mb={4}>
                    COPY EXAMPLES
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <Box p={4} bg="rgba(48, 209, 88, 0.1)" borderRadius="xl" border="1px solid rgba(48, 209, 88, 0.3)">
                      <Text color="#30D158" fontSize="xs" fontWeight="600" mb={2}>
                        GOOD
                      </Text>
                      <Text color="white" fontStyle="italic">
                        "Build retirement wealth beyond superannuation with data-driven
                        property investment strategies."
                      </Text>
                    </Box>
                    <Box p={4} bg="rgba(255, 59, 48, 0.1)" borderRadius="xl" border="1px solid rgba(255, 59, 48, 0.3)">
                      <Text color="#FF3B30" fontSize="xs" fontWeight="600" mb={2}>
                        AVOID
                      </Text>
                      <Text color="white" fontStyle="italic">
                        "Get rich fast with our AMAZING property deals! Don't miss out —
                        ACT NOW!"
                      </Text>
                    </Box>
                  </SimpleGrid>
                </GlassCard>
              </VStack>
            </TabPanel>

            {/* Usage Panel */}
            <TabPanel px={0}>
              <VStack spacing={12} align="stretch">
                {/* Color Usage */}
                <GlassCard>
                  <SectionHeader
                    icon={FaPalette}
                    title="Color Application Rules"
                    subtitle="When and how to use each color"
                  />

                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th color="gray.400" borderColor="whiteAlpha.100">
                          Element
                        </Th>
                        <Th color="gray.400" borderColor="whiteAlpha.100">
                          Color
                        </Th>
                        <Th color="gray.400" borderColor="whiteAlpha.100">
                          Usage
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {[
                        ["Primary CTA", "#E8B923", "Book Consultation, Get Started, Main conversions"],
                        ["Secondary CTA", "#14B8A6", "Learn More, View Details, Secondary actions"],
                        ["Tertiary CTA", "White outline", "Low-priority actions, Cancel, Back"],
                        ["Hero overlays", "rgba(0,31,63,0.85)", "Image backgrounds with navy overlay"],
                        ["Solid sections", "#001f3f", "Stats, features, process steps"],
                        ["Page backgrounds", "#001f3f gradient", "Full page navy gradient"],
                        ["Card backgrounds", "#FFFFFF or glass", "Content containers on navy BG"],
                        ["Success states", "#30D158", "Confirmations, completed actions"],
                        ["Warnings", "#FF9F0A", "Caution, pending items"],
                        ["Errors", "#FF3B30", "Failed actions, validation errors"],
                        ["Links (dark BG)", "#E8B923", "Interactive text on navy backgrounds"],
                        ["Links (light BG)", "#101c47", "Interactive text on light backgrounds"],
                      ].map(([element, color, usage], idx) => (
                        <Tr key={idx}>
                          <Td color="white" borderColor="whiteAlpha.100" fontWeight="600">
                            {element}
                          </Td>
                          <Td borderColor="whiteAlpha.100">
                            <Code bg="whiteAlpha.100" color={color.startsWith("#") ? color : "white"} px={2}>
                              {color}
                            </Code>
                          </Td>
                          <Td color="gray.400" borderColor="whiteAlpha.100">
                            {usage}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </GlassCard>

                {/* Logo Usage */}
                <GlassCard>
                  <SectionHeader
                    icon={FaEye}
                    title="Logo Guidelines"
                    subtitle="Proper logo application"
                    color="#14B8A6"
                  />

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                    <Box p={6} bg="#001f3f" borderRadius="xl" textAlign="center">
                      <Box
                        as="img"
                        src={brandConfig.logo.src}
                        alt={brandConfig.logo.alt}
                        maxH="60px"
                        mx="auto"
                        mb={4}
                      />
                      <Text color="white" fontSize="sm">
                        Primary — On Deep Navy
                      </Text>
                      <Code fontSize="xs" bg="whiteAlpha.100" color="gray.400" mt={1}>
                        #001f3f
                      </Code>
                    </Box>
                    <Box p={6} bg="white" borderRadius="xl" textAlign="center">
                      <Box
                        as="img"
                        src={brandConfig.logo.src}
                        alt={brandConfig.logo.alt}
                        maxH="60px"
                        mx="auto"
                        mb={4}
                      />
                      <Text color="gray.600" fontSize="sm">
                        Primary — On White
                      </Text>
                      <Code fontSize="xs" bg="gray.100" color="gray.500" mt={1}>
                        #FFFFFF
                      </Code>
                    </Box>
                    <Box
                      p={6}
                      borderRadius="xl"
                      textAlign="center"
                      position="relative"
                      overflow="hidden"
                      backgroundImage="url('https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=400&q=80')"
                      backgroundSize="cover"
                      backgroundPosition="center"
                      _before={{
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bg: "rgba(0, 31, 63, 0.85)",
                      }}
                    >
                      <Box position="relative" zIndex={1}>
                        <Box
                          as="img"
                          src={brandConfig.logo.src}
                          alt={brandConfig.logo.alt}
                          maxH="60px"
                          mx="auto"
                          mb={4}
                        />
                        <Text color="white" fontSize="sm">
                          On Image + Overlay
                        </Text>
                        <Code fontSize="xs" bg="whiteAlpha.100" color="gray.400" mt={1}>
                          rgba(0,31,63,0.85)
                        </Code>
                      </Box>
                    </Box>
                  </SimpleGrid>

                  <Alert status="info" mt={6} borderRadius="xl" bg="rgba(0, 122, 255, 0.1)" border="1px solid rgba(0, 122, 255, 0.3)">
                    <AlertIcon color="#007AFF" />
                    <Text color="white" fontSize="sm">
                      Always maintain minimum clear space around the logo equal to the height
                      of the "O" in One Group.
                    </Text>
                  </Alert>
                </GlassCard>

                {/* Spacing & Layout */}
                <GlassCard>
                  <SectionHeader
                    icon={FaSquare}
                    title="Spacing System"
                    subtitle="Consistent rhythm and hierarchy"
                    color="#E8B923"
                  />

                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                    {[
                      { size: "4px", name: "xs", use: "Tight grouping" },
                      { size: "8px", name: "sm", use: "Related elements" },
                      { size: "16px", name: "md", use: "Standard spacing" },
                      { size: "24px", name: "lg", use: "Section gaps" },
                      { size: "32px", name: "xl", use: "Major sections" },
                      { size: "48px", name: "2xl", use: "Page sections" },
                      { size: "64px", name: "3xl", use: "Hero spacing" },
                      { size: "96px", name: "4xl", use: "Major breaks" },
                    ].map((space) => (
                      <Box
                        key={space.name}
                        p={4}
                        bg="whiteAlpha.50"
                        borderRadius="xl"
                        textAlign="center"
                      >
                        <Box
                          w={space.size}
                          h={space.size}
                          bg="#E8B923"
                          mx="auto"
                          mb={3}
                          borderRadius="sm"
                        />
                        <Text color="white" fontWeight="bold">
                          {space.name}
                        </Text>
                        <Text color="gray.500" fontSize="xs">
                          {space.size}
                        </Text>
                        <Text color="gray.400" fontSize="xs" mt={1}>
                          {space.use}
                        </Text>
                      </Box>
                    ))}
                  </SimpleGrid>
                </GlassCard>

                {/* Quick Reference */}
                <GlassCard>
                  <SectionHeader
                    icon={FaLightbulb}
                    title="Quick Reference"
                    subtitle="Copy-paste ready values"
                    color="#30D158"
                  />

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    {[
                      { label: "Deep Navy (Hero BG)", value: "#001f3f" },
                      { label: "Navy Overlay", value: "rgba(0,31,63,0.85)" },
                      { label: "Navy Primary", value: "#101c47" },
                      { label: "Primary Gold", value: "#E8B923" },
                      { label: "Gold Hover", value: "#D4A843" },
                      { label: "Clarity Teal", value: "#14B8A6" },
                      { label: "Teal Hover", value: "#0D9488" },
                      { label: "Success Green", value: "#30D158" },
                      { label: "Warning Orange", value: "#FF9F0A" },
                      { label: "Error Red", value: "#FF3B30" },
                      { label: "Info Blue", value: "#007AFF" },
                    ].map((item) => (
                      <HStack
                        key={item.label}
                        p={3}
                        bg="whiteAlpha.50"
                        borderRadius="lg"
                        justify="space-between"
                        cursor="pointer"
                        _hover={{ bg: "whiteAlpha.100" }}
                        onClick={() => {
                          navigator.clipboard.writeText(item.value);
                          toast({
                            title: "Copied!",
                            description: `${item.value}`,
                            status: "success",
                            duration: 1500,
                          });
                        }}
                      >
                        <HStack>
                          <Box w="20px" h="20px" bg={item.value} borderRadius="md" />
                          <Text color="white" fontSize="sm">
                            {item.label}
                          </Text>
                        </HStack>
                        <Code fontSize="xs" bg="whiteAlpha.100" color="gray.300" px={2}>
                          {item.value}
                        </Code>
                      </HStack>
                    ))}
                  </SimpleGrid>
                </GlassCard>
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Container>

      {/* Global styles for animations */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
        `}
      </style>
    </>
  );
};

export default BrandStyleGuide;
